'use client';

import { FRONTIFY_URL, isKenoGoBrand } from '@shared/utils';
import Image from 'next/image';
import { LargeLogo } from '../../../../Logos/Logos';
import { FALLBACK_OFFER_IMAGE } from './constants';

const KENOGO_BANNER_STRIPES_URL = `${FRONTIFY_URL}XhCHDQMPDmmKPBhWTP2A.png`;
const KENOGO_BANNER_LOGO_URL = `${FRONTIFY_URL}QouxCBCxRjmhYxWXHc18.svg`;
const KENOGO_BANNER_TAGLINE_URL = `${FRONTIFY_URL}r9RbGrC3NNPuPqosb2tC.svg`;
const KENOGO_BANNER_PEOPLE_URL = `${FRONTIFY_URL}xCXxaAwMozFbMNmtpnuG.png`;

function OfferPrimaryFallbackKenoGo() {
  return (
    <div
      className="relative flex flex-col h-[220px] md:h-full overflow-hidden bg-accent-green text-foreground-inverse dark:text-foreground"
      data-testid="offer-primary-fallback-container"
    >
      {/* Green fill + geometric stripes — desktop */}
      <div className="hidden md:block absolute inset-0" aria-hidden>
        <Image src={KENOGO_BANNER_STRIPES_URL} alt="" fill className="object-cover" sizes="50vw" priority unoptimized data-testid="kenogo-banner-stripes" />
      </div>

      {/* Mobile — stripes background */}
      <div className="md:hidden absolute inset-0" aria-hidden>
        <Image src={KENOGO_BANNER_STRIPES_URL} alt="" fill className="object-cover" sizes="100vw" priority unoptimized data-testid="kenogo-banner-stripes" />
      </div>

      {/* Mobile — people: full-width, anchored to bottom */}
      <div className="md:hidden absolute inset-x-0 bottom-0 top-[20px] pointer-events-none" aria-hidden>
        <Image src={KENOGO_BANNER_PEOPLE_URL} alt="" aria-hidden fill className="object-contain object-bottom" sizes="100vw" priority unoptimized data-testid="offer-image" />
      </div>

      {/* Mobile — KenoGO wordmark: clipped by header's overflow-hidden, not its own */}
      <div className="md:hidden absolute bottom-0 right-14 z-10 max-h-[63px] w-[138px] pointer-events-none">
        <div className="origin-top-left scale-[0.44]">
          <Image
            src={KENOGO_BANNER_LOGO_URL}
            alt="KenoGO"
            width={1040}
            height={1254}
            className="h-auto w-[1040px] max-w-none"
            sizes="138px"
            priority
            unoptimized
            data-testid="offer-primary-fallback-logo"
          />
        </div>
      </div>

      {/* KenoGO wordmark + tagline — desktop only (full SVG artwork incl. decorative shapes below text) */}
      {/* w uses max(%, dvh) so the artwork fills the panel height even on tall/narrow viewports */}
      <div className="hidden md:block absolute inset-0 z-10 pointer-events-none">
        <div className="absolute left-1/2 top-[22%] w-[max(82%,_70dvh)] -translate-x-[15%] origin-top scale-[1.22]">
          <Image
            src={KENOGO_BANNER_LOGO_URL}
            alt="KenoGO"
            width={1040}
            height={1254}
            className="h-auto w-full"
            sizes="50vw"
            priority
            unoptimized
            data-testid="offer-primary-fallback-logo"
          />
        </div>
        <div className="absolute left-1/2 top-[36%] w-[max(86%,_56dvh)] -translate-x-[38%] origin-top scale-[1.0]">
          <Image
            src={KENOGO_BANNER_TAGLINE_URL}
            alt="Turn your wait time into play time"
            width={1346}
            height={1282}
            className="h-auto w-full"
            sizes="50vw"
            priority
            unoptimized
            data-testid="kenogo-banner-tagline"
          />
        </div>
      </div>

      {/* People cutout — desktop only */}
      <div className="hidden md:block absolute inset-x-0 bottom-0 top-[32%] pointer-events-none z-20">
        <div className="relative flex h-full items-end justify-center">
          <Image
            src={KENOGO_BANNER_PEOPLE_URL}
            alt=""
            aria-hidden
            width={700}
            height={480}
            className="h-auto max-h-full w-[max(80%,_55dvh)] object-contain object-bottom"
            sizes="700px"
            priority
            unoptimized
            data-testid="offer-image"
          />
        </div>
      </div>
    </div>
  );
}

function OfferPrimaryFallbackDefault() {
  return (
    <div
      className="flex flex-col h-full py-6 px-6 md:py-12 md:px-10 md:pb-0 bg-foreground dark:bg-container-base text-foreground-inverse dark:text-foreground overflow-hidden"
      data-testid="offer-primary-fallback-container"
    >
      {/* Logo */}
      <div className="md:mb-8">
        <div className="relative w-40 h-12 md:w-44 md:h-13">
          <LargeLogo className="w-full h-full text-accent-green" data-testid="offer-primary-fallback-logo" />
        </div>
      </div>

      <div className="hidden md:flex justify-center items-center pb-16 h-[calc(100vh-132px)]">
        {/* Image - hidden on mobile, only desktop image used */}
        <div className="relative max-w-132.5 w-full aspect-[530/280] rounded-2xl mx-auto">
          <Image
            src={FALLBACK_OFFER_IMAGE}
            alt="Offer primary fallback background"
            fill
            className="rounded-2xl object-cover"
            sizes="(max-width: 768px) 100vw, 530px"
            priority
            unoptimized
            data-testid="offer-image"
          />
        </div>
      </div>
    </div>
  );
}

function OfferPrimaryFallback() {
  if (isKenoGoBrand()) {
    return <OfferPrimaryFallbackKenoGo />;
  }
  return <OfferPrimaryFallbackDefault />;
}

export { OfferPrimaryFallback };
